import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  useCreateTextLeveler,
  useGetTextLeveler,
} from "@packages/api/madlenAPI";
import { getEnvironment } from "@packages/utils";
import { useToast } from "@packages/contexts/ToastContext";
import TextLevelerCard from "../../../components/text_leveler/TextLevelerCard";
import ErrorComponent from "../../../components/error-component";
import LevelSelector from "../../../components/text_leveler/LevelSelector";
import { Button, Text } from "@core/index";
import {
  TeacherDocumentResponseDto,
  TextLevelerCreateRequestDtoGenerationType,
  TextLevelerCreateRequestDtoContentLanguage,
  TextLevelerCreateRequestDtoCefrLevel,
} from "@packages/api/madlenAPI.schemas";
import LoadingContainer from "@packages/components/Loading";
import { FileUploader } from "@core/file-uploader";
import { trackEvent } from "@packages/hooks/useAnalytics";
import ModeToggle from "../../../../common/components/mode-toggle";
import { updateLastUsedTools } from "@packages/store/userSlice";
import { useAppDispatch } from "@packages/store/hooks";
import CEFRSelector from "@core/cefr-selector/CEFRSelector";
import enFlag from "@packages/assets/en.png";
import trFlag from "@packages/assets/tr.png";
import deFlag from "@packages/assets/de.png";
import frFlag from "@packages/assets/fr.png";

type GeneratorMode = "document" | "text";

const CHARACTER_LIMIT = 200000;

const CreateTextLevelerView = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [input, setInput] = useState({
    prompt: "",
  });
  const [generatorMode, setGeneratorMode] = useState<GeneratorMode>("text");
  const [contentLanguage, setContentLanguage] = useState<
    "en" | "tr" | "de" | "fr"
  >("en");
  const [cefrLevel, setCefrLevel] =
    useState<TextLevelerCreateRequestDtoCefrLevel>(
      TextLevelerCreateRequestDtoCefrLevel.A1,
    );

  const { mutateAsync: createTextLeveler, data: textLeveler } =
    useCreateTextLeveler();
  const { addToast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<
    TeacherDocumentResponseDto[]
  >([]);

  const acceptedFileTypes = {
    "application/pdf": [".pdf"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
      ".docx",
    ],
    "text/plain": [".txt"],
    "image/png": [".png"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/jpg": [".jpg", ".jpeg"],
  };

  const { data: textLevelerData } = useGetTextLeveler(textLeveler?.id ?? 0, {
    query: {
      enabled: !!textLeveler?.id,
      refetchInterval(query) {
        if (
          !query.state.data?.leveledText ||
          !query.state.data?.grade ||
          !query.state.data?.imageUrl
        ) {
          return 2000;
        }
        setIsLoading(false);
        return false;
      },
    },
  });

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (
      (generatorMode === "text" && input.prompt.trim() === "") ||
      (generatorMode === "document" && uploadedFiles.length === 0)
    ) {
      addToast({
        duration: 5000,
        title: t("textLeveler.create.error.empty"),
        variant: "error",
      });
      return;
    }

    if (generatorMode === "text" && input.prompt.length > CHARACTER_LIMIT) {
      addToast({
        duration: 5000,
        title: t("textLeveler.create.error.tooLong"),
        description: t("textLeveler.create.error.characterLimit", {
          limit: CHARACTER_LIMIT.toLocaleString(),
        }),
        variant: "error",
      });
      return;
    }

    const teacherDocumentIds = uploadedFiles
      .map((file) => file.id)
      .filter((id): id is number => id !== undefined);

    try {
      setIsLoading(true);
      setError(null);

      const result = await createTextLeveler({
        data: {
          prompt: input.prompt,
          environment: getEnvironment(),
          teacherDocumentIds,
          contentLanguage:
            contentLanguage as TextLevelerCreateRequestDtoContentLanguage,
          cefrLevel:
            contentLanguage === "en"
              ? cefrLevel
              : TextLevelerCreateRequestDtoCefrLevel.A1,
          generationType:
            generatorMode === "document"
              ? TextLevelerCreateRequestDtoGenerationType.TEACHER_DOCUMENT
              : TextLevelerCreateRequestDtoGenerationType.PROMPT,
        },
      });

      if (!result?.id) {
        console.error("No text leveler ID returned");
      }
      dispatch(updateLastUsedTools("text-leveler"));
    } catch (error: any) {
      setIsLoading(false);
      if (
        error.response?.data.message ===
        "Content contains inappropriate language"
      ) {
        addToast({
          title: t("common.inappropriateContent.title"),
          description: t("common.inappropriateContent.description"),
          variant: "error",
          duration: 3000,
        });
      } else {
        setError(error as Error);
        console.error("Text leveler error:", error);
        addToast({
          title: t("textLeveler.create.error.title"),
          description: t("textLeveler.create.error.description"),
          variant: "error",
        });
      }
    }
  };

  const handleFileUploadError = (error: string) => {
    addToast({
      title: t("assistant.uploadError"),
      description: error,
      variant: "error",
      duration: 5000,
    });
  };

  const { trackEvent: track } = trackEvent();

  const handleFileUploadSuccess = async (
    url: string,
    response?: TeacherDocumentResponseDto,
  ) => {
    track(
      {
        category: "ContentFileUpload",
        action: `text-leveler.create.file.upload.success`,
        label: `TextLevelerCreate file upload success`,
        nonInteraction: false,
      },
      {
        location: "/text-leveler-create/file-upload",
        element: "file-upload",
      },
    );

    if (!response) {
      console.error("No response received from upload");
      addToast({
        title: t("assistant.uploadError"),
        description: t("assistant.uploadErrorDescription"),
        variant: "error",
        duration: 5000,
      });
      return;
    }

    setUploadedFiles((prevFiles) => {
      const newFiles = [...prevFiles, response];
      return newFiles;
    });
  };

  if (textLevelerData?.leveledText) {
    return (
      <div className="flex-1 p-3 sm:p-4 md:p-6 lg:p-8 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
        <div className="max-w-7xl mx-auto">
          <div className="mb-4 sm:mb-6 lg:mb-8">
            <Button
              onClick={() => navigate("/content/text-leveler")}
              size="md"
              rounded="full"
              bgColor="bg-white dark:bg-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700"
              textColor="text-gray-700 dark:text-gray-200"
              variant="outline"
              className="border-gray-300 dark:border-gray-600"
              leftIcon={<ArrowLeft size={20} />}
            >
              {t("common.back")}
            </Button>
          </div>

          <TextLevelerCard
            onCreateAgain={() => {
              handleSubmit();
            }}
            createdAt={textLevelerData?.createdAt ?? ""}
            content={textLevelerData?.leveledText ?? ""}
            level={String(textLevelerData?.grade ?? 1)}
            imageUrl={textLevelerData?.imageUrl}
            author={textLevelerData?.authorName ?? ""}
          />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <LoadingContainer
        data-testid="text-leveler-loading-dot"
        title={t("textLeveler.loading.title")}
        themeColor="blue"
      />
    );
  }

  if (error) {
    return (
      <ErrorComponent
        title={t("textLeveler.create.error.title")}
        description={t("textLeveler.create.error.description")}
      />
    );
  }

  return (
    <div className="flex-1 p-3 sm:p-4 md:p-6 lg:p-8 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        {/* Header with title and back button */}
        <div className="mb-4 sm:mb-6 lg:mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
          <div className="min-w-0 flex-1">
            <Text
              variant="h1"
              className="text-lg sm:text-xl lg:text-2xl xl:text-3xl break-words"
            >
              {t("textLeveler.create.title")}
            </Text>
            <Text
              variant="p"
              className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base break-words"
            >
              {t("textLeveler.create.description")}
            </Text>
          </div>
          <div className="flex-shrink-0">
            <Button
              onClick={() => navigate("/content/text-leveler")}
              size="md"
              rounded="full"
              bgColor="bg-white dark:bg-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700"
              textColor="text-gray-700 dark:text-gray-200"
              variant="outline"
              className="border-gray-300 dark:border-gray-600"
              leftIcon={<ArrowLeft size={20} />}
            >
              {t("common.back")}
            </Button>
          </div>
        </div>

        {/* Main content card */}
        <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-dark-200 dark:border-dark-400 p-3 sm:p-4 lg:p-6 mt-3 sm:mt-4 lg:mt-6">
          <Text
            variant="h2"
            className="mb-3 sm:mb-4 text-base sm:text-lg lg:text-xl xl:text-2xl break-words"
          >
            {t("textLeveler.create.formTitle")}
          </Text>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Input mode toggle */}
            <ModeToggle
              value={generatorMode}
              onChange={(value) => {
                setGeneratorMode(value as GeneratorMode);
                setUploadedFiles([]);
                setInput({ ...input, prompt: "" });
              }}
              title={t("textLeveler.create.selectInputType")}
              theme="blue"
            />

            {/* Content Language and CEFR Level in grid */}
            <div className="">
              {/* Content Language Selector */}
              <div className="space-y-3 hidden">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t("textLeveler.contentLanguage")}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    onClick={() => setContentLanguage("tr")}
                    rounded="xl"
                    bgColor={
                      contentLanguage === "tr"
                        ? "bg-blue-50 dark:bg-blue-900"
                        : ""
                    }
                    className={`p-3 border-2 ${
                      contentLanguage === "tr"
                        ? "border-blue-500 dark:border-blue-400"
                        : "border-gray-200 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-400"
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <img
                        src={trFlag}
                        alt="Turkish"
                        className="w-6 h-4 object-cover rounded"
                      />
                      <span className="text-sm sm:text-base">Türkçe</span>
                    </div>
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setContentLanguage("en")}
                    rounded="xl"
                    bgColor={
                      contentLanguage === "en"
                        ? "bg-blue-50 dark:bg-blue-900"
                        : ""
                    }
                    className={`p-3 border-2 ${
                      contentLanguage === "en"
                        ? "border-blue-500 dark:border-blue-400"
                        : "border-gray-200 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-400"
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <img
                        src={enFlag}
                        alt="English"
                        className="w-6 h-4 object-cover rounded"
                      />
                      <span className="text-sm sm:text-base">English</span>
                    </div>
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setContentLanguage("de")}
                    rounded="xl"
                    bgColor={
                      contentLanguage === "de"
                        ? "bg-blue-50 dark:bg-blue-900"
                        : ""
                    }
                    className={`p-3 border-2 ${
                      contentLanguage === "de"
                        ? "border-blue-500 dark:border-blue-400"
                        : "border-gray-200 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-400"
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <img
                        src={deFlag}
                        alt="German"
                        className="w-6 h-4 object-cover rounded"
                      />
                      <span className="text-sm sm:text-base">Deutsch</span>
                    </div>
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setContentLanguage("fr")}
                    rounded="xl"
                    bgColor={
                      contentLanguage === "fr"
                        ? "bg-blue-50 dark:bg-blue-900"
                        : ""
                    }
                    className={`p-3 border-2 ${
                      contentLanguage === "fr"
                        ? "border-blue-500 dark:border-blue-400"
                        : "border-gray-200 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-400"
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <img
                        src={frFlag}
                        alt="French"
                        className="w-6 h-4 object-cover rounded"
                      />
                      <span className="text-sm sm:text-base">Français</span>
                    </div>
                  </Button>
                </div>
              </div>

              {/* CEFR Level Selector - Only show for English */}
              {contentLanguage === "en" && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t("textLeveler.cefrLevel")}
                  </label>
                  <CEFRSelector
                    selectedLevel={cefrLevel}
                    onLevelChange={setCefrLevel}
                    themeColor="blue"
                    className="mt-1"
                  />
                </div>
              )}
            </div>

            {/* Input content area */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {generatorMode === "text"
                  ? t("textLeveler.create.prompt")
                  : t("textLeveler.create.uploadDocumentTitle")}
              </label>

              {generatorMode === "document" && (
                <FileUploader
                  accept={acceptedFileTypes}
                  onUploadSuccess={handleFileUploadSuccess}
                  onFileDelete={() => setUploadedFiles([])}
                  onUploadError={handleFileUploadError}
                  dragActiveText={t("textLeveler.create.dropDocument")}
                  dragInactiveText={t("textLeveler.create.uploadDocument")}
                  uploadColor="blue"
                  extractColor="blue"
                  successColor="blue"
                  shouldExtractContent
                />
              )}

              {generatorMode === "text" && (
                <textarea
                  value={input.prompt}
                  data-testid="text-input"
                  onChange={(e) =>
                    setInput({ ...input, prompt: e.target.value })
                  }
                  rows={12}
                  className="w-full rounded-xl border-2 border-gray-200 dark:border-gray-600 p-4 text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-dark-500 dark:text-gray-100 transition-all duration-200 placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder={t("textLeveler.create.promptPlaceholder")}
                />
              )}
            </div>
          </form>
        </div>

        {/* Generate button */}
        <div className="mt-4 sm:mt-6 flex justify-end">
          <Button
            onClick={handleSubmit}
            size="md"
            rounded="full"
            bgColor="bg-blue-500 dark:bg-blue-500 hover:bg-blue-600 dark:hover:bg-blue-600"
            textColor="text-white"
            className={`px-6 sm:px-8 lg:px-12 ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
            disabled={
              isLoading ||
              (generatorMode === "text" && !input.prompt) ||
              (generatorMode === "document" && uploadedFiles.length === 0)
            }
            data-testid="analyze-text-button"
          >
            {t("textLeveler.create.analyzeText")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateTextLevelerView;
